import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "../cn";
import "./side-sheet.css";

export interface SideSheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** A control above the title, such as a way back, outside the heading. */
  eyebrow?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  footerClassName?: string;
  className?: string;
  /** Keep unsaved work open until the caller's own action dismisses it. */
  dismissible?: boolean;
  /**
   * `card` is a 440px card inset 8px from the window's edges, with a 16px
   * radius: a `text-prose` title (the eyebrow a small mono line above it), a
   * borderless 30px close mark, a 20px body whose children sit 16px apart, the
   * actions at the right, and a lighter scrim. Its body becomes a focusable,
   * labelled region while its content overflows, so a keyboard can scroll it.
   * Opt-in; `panel` is the default.
   */
  look?: "panel" | "card";
  /** `card` only: as tall as its content, up to the window, rather than the window's full height. */
  fit?: boolean;
  /** `card` only: classes for the title, which win over its defaults. */
  titleClassName?: string;
}

/**
 * A full-height modal for reading or working through content longer than a dialog.
 * 24px gutters on header, body and footer; the title is `text-title`.
 */
export function SideSheet({
  open,
  onClose,
  title,
  description,
  eyebrow,
  children,
  footer,
  footerClassName,
  className,
  dismissible = true,
  look = "panel",
  fit = false,
  titleClassName,
}: SideSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [bodyRef, scrolls] = useOverflows(look === "card");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      const opener = document.activeElement as HTMLElement;
      el.showModal();
      // Cancel can unmount a sheet without the browser restoring its opener.
      return () => opener.focus();
    }
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cancel = (event: Event) => {
      if (!dismissible) event.preventDefault();
    };
    el.addEventListener("close", onClose);
    el.addEventListener("cancel", cancel);
    return () => {
      el.removeEventListener("close", onClose);
      el.removeEventListener("cancel", cancel);
    };
  }, [onClose, dismissible]);

  if (look === "card") {
    return (
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
      <dialog
        ref={ref}
        aria-labelledby={titleId}
        className={cn(
          "fixed top-2 right-2 left-auto m-0 ml-auto w-[min(440px,calc(100%-1rem))] max-w-none grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden",
          fit
            ? "bottom-auto h-auto max-h-[calc(100dvh-1rem)]"
            : "bottom-2 h-[calc(100dvh-1rem)] max-h-none",
          "rounded-2xl border border-rule bg-surface p-0 text-ink shadow-float",
          "open:grid open:animate-[mw-side-sheet-enter_240ms_ease-out_both] motion-reduce:open:animate-none",
          "backdrop:bg-scrim-sheet",
          className,
        )}
        onClick={(event) => {
          if (dismissible && event.target === ref.current) onClose();
        }}
      >
        <header className="flex items-center justify-between gap-1.5 border-b border-rule py-3.5 pr-4 pl-5">
          <div className="flex min-w-0 flex-col gap-0.5">
            {eyebrow ? (
              <p className="font-mono text-micro tracking-[0.08em] text-ink-3 uppercase">
                {eyebrow}
              </p>
            ) : null}
            <h2
              id={titleId}
              className={cn("text-prose leading-[1.35] font-semibold text-ink", titleClassName)}
            >
              {title}
            </h2>
            {description ? <p className="text-meta text-ink-3">{description}</p> : null}
          </div>
          {dismissible ? (
            <button
              type="button"
              aria-label="Close panel"
              onClick={onClose}
              className="grid size-7.5 shrink-0 place-items-center rounded-[var(--radius-control)] text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          ) : null}
        </header>
        <div
          ref={bodyRef}
          data-side-sheet-body
          tabIndex={scrolls ? 0 : undefined}
          role={scrolls ? "region" : undefined}
          aria-labelledby={scrolls ? titleId : undefined}
          className="min-h-0 overflow-y-auto p-5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
        >
          <div className="flex flex-col gap-4">{children}</div>
        </div>
        {footer ? (
          <footer
            className={cn(
              "flex flex-wrap items-center justify-end gap-2 border-t border-rule px-4 py-3",
              footerClassName,
            )}
          >
            {footer}
          </footer>
        ) : null}
      </dialog>
    );
  }

  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cn(
        "fixed inset-y-0 right-0 left-auto m-0 ml-auto h-dvh max-h-dvh w-full max-w-[42rem] grid-rows-[auto_minmax(0,1fr)_auto] border-l border-rule-strong bg-surface p-0 text-ink open:grid open:animate-[mw-side-sheet-enter_240ms_ease-out_both] motion-reduce:open:animate-none",
        "backdrop:bg-black/55", // token-exempt: modal scrim darkens the content beneath it
        className,
      )}
      onClick={(event) => {
        if (dismissible && event.target === ref.current) onClose();
      }}
    >
      <header className="flex items-start justify-between gap-4 border-b border-rule px-6 pt-5.5 pb-4">
        <div>
          {eyebrow}
          <h2 id={titleId} className="text-title font-semibold text-ink">
            {title}
          </h2>
          {description ? <p className="mt-1 text-meta text-ink-2">{description}</p> : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            aria-label="Close panel"
            onClick={onClose}
            className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)] border border-rule text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        ) : null}
      </header>
      <div data-side-sheet-body className="min-h-0 overflow-y-auto px-6 py-5">
        {children}
      </div>
      {footer ? (
        <footer
          className={cn(
            "flex flex-wrap items-center gap-2 border-t border-rule px-6 pt-3.5 pb-5",
            footerClassName,
          )}
        >
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}

/** Whether an element's content is taller than it, watched only while `watch` is set. */
function useOverflows(watch: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!watch || !el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setOverflows(el.scrollHeight > el.clientHeight));
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [watch]);
  return [ref, overflows] as const;
}
